// La pantalla «Santi» en la pared de la oficina (propuesta §5.7, P5): la misma caja que Calypso, Cerebros y Cuentas
// (176 de ancho, borde de tinta, anillo de oro oscuro; letra 11 / 22 / 11). Callada por defecto: UN estado a la vez, en
// este orden — algo de Pau que no llegó a Santi («Sin enviar» y qué, en rojo: un «Hecho» o «Cancelar» que falló, aunque
// la ventana estuviera cerrada; abre la ventana, que tiene arriba «Volver a probar») › venció (rojo, abre ese contrato)
// › vence hoy (naranja, abre ese) › algo nuevo de Santi («2 nuevos» y dónde,
// en blanco; abre lo más reciente) › sin conexión (en lugar de «Nada nuevo»: no se puede decir que no hay nada) › nada
// nuevo (gris; abre el último que tenías abierto). «Nuevos» cuenta contratos (y el chat) con algo de Santi sin ver: la
// misma cifra que los puntos blancos de la lista. Fuera de la pared: «N te tocan», «N abiertos», «parado», ⚠ y los días.
// Si la oficina no está conectada al tablero, no ocupa sitio.
import { WIDGET } from '../ui';
import { useEffect } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { hace } from '../marco/estado';
import { hiloAlAbrir, usePelotasStore, type FalloActo } from './store';
import type { HiloAbierto, ListaContratosDTO } from './tipos';

export interface EstadoPared {
  estado: 'sinEnviar' | 'vencido' | 'venceHoy' | 'nuevos' | 'sinConexion' | 'nada';
  punto: string;
  grande: string;
  /** «nuevo» / «nuevos» al lado de la cifra. */
  chico?: string;
  claseGrande: string;
  pie: string;
  clasePie: string;
  abre: HiloAbierto;
}

const tituloDe = (lista: ListaContratosDTO, hilo: number | 'general' | null): string =>
  hilo === 'general' ? t('oficinaV2.pelotas.lista.chat') : hilo === null ? '' : (lista.contratos.find((c) => c.temaId === hilo)?.titulo ?? '');

/** El estado de la pared a partir del resumen del servidor (`pared`) y de lo que no llegó. Puro: lo prueba PantallaSanti.test. */
export function estadoPared(lista: ListaContratosDTO, todos: Pick<FalloActo, 'temaId' | 'titulo' | 'explicado'>[] = []): EstadoPared {
  const p = lista.pared;
  // Una respuesta del centro de antes (NO_ES_TUYO, TABLERO_SIN_CANCELAR) no es «Sin enviar»: la explica la ventana.
  const fallos = todos.filter((f) => !f.explicado);
  if (fallos.length) {
    const ultimo = fallos[fallos.length - 1]!;
    return {
      estado: 'sinEnviar',
      punto: 'bg-oficina-error',
      grande: t('oficinaV2.pelotas.pantalla.sinEnviar'),
      claseGrande: 'text-oficina-error',
      pie: fallos.length > 1 ? t('oficinaV2.pelotas.pantalla.yMas', { titulo: ultimo.titulo, n: fallos.length - 1 }) : ultimo.titulo,
      clasePie: 'text-oficina-texto',
      abre: ultimo.temaId,
    };
  }
  if (p.vencido !== null) {
    return { estado: 'vencido', punto: 'bg-oficina-error', grande: t('oficinaV2.pelotas.pantalla.vencio'), claseGrande: 'text-oficina-error', pie: tituloDe(lista, p.vencido), clasePie: 'text-oficina-texto2', abre: p.vencido };
  }
  if (p.venceHoy !== null) {
    return { estado: 'venceHoy', punto: 'bg-oficina-bloqueado', grande: t('oficinaV2.pelotas.pantalla.venceHoy'), claseGrande: 'text-oficina-bloqueado', pie: tituloDe(lista, p.venceHoy), clasePie: 'text-oficina-texto2', abre: p.venceHoy };
  }
  if (p.nuevos > 0) {
    const donde = tituloDe(lista, p.masReciente);
    return {
      estado: 'nuevos',
      punto: 'bg-oficina-texto',
      grande: String(p.nuevos),
      chico: t(p.nuevos === 1 ? 'oficinaV2.pelotas.pantalla.nuevo' : 'oficinaV2.pelotas.pantalla.nuevos'),
      claseGrande: 'text-oficina-texto',
      pie: p.nuevos > 1 && donde ? t('oficinaV2.pelotas.pantalla.yMas', { titulo: donde, n: p.nuevos - 1 }) : donde,
      clasePie: 'text-oficina-texto',
      abre: hiloAlAbrir(lista),
    };
  }
  if (lista.centroCaido) {
    return { estado: 'sinConexion', punto: 'bg-oficina-esperando', grande: t('oficinaV2.pelotas.pantalla.sinConexion'), claseGrande: 'text-oficina-texto3', pie: hace(lista.servidoEn), clasePie: 'text-oficina-texto3', abre: hiloAlAbrir(lista) };
  }
  return { estado: 'nada', punto: 'bg-oficina-situacion-apagado', grande: t('oficinaV2.pelotas.pantalla.nadaNuevo'), claseGrande: 'text-oficina-texto3', pie: '', clasePie: 'text-oficina-texto3', abre: hiloAlAbrir(lista) };
}

export function PantallaSanti() {
  const lista = usePelotasStore((s) => s.lista);
  const fallos = usePelotasStore((s) => s.fallos);
  const cargar = usePelotasStore((s) => s.cargarLista);
  const abrirSanti = usePelotasStore((s) => s.abrirSanti);
  useEffect(() => {
    void cargar();
  }, [cargar]);
  if (!lista?.configurado) return null;
  const e = estadoPared(lista, fallos);
  return <AzulejoSanti e={e} alPulsar={() => abrirSanti(e.abre)} />;
}

/** La caja de la pared, pintada a partir de su estado (la usan la pared y las historias de Storybook). */
export function AzulejoSanti({ e, alPulsar }: { e: EstadoPared; alPulsar: () => void }) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      aria-label={[t('oficinaV2.pelotas.pantalla.titulo'), [e.grande, e.chico].filter(Boolean).join(' '), e.pie, t('oficinaV2.pelotas.pantalla.abrir')].filter(Boolean).join('. ')}
      className={WIDGET}
      data-testid="pantalla-santi"
      data-estado={e.estado}
    >
      <span className="flex items-center gap-1.5 truncate text-[11px] leading-4 text-oficina-texto2">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', e.punto)} data-testid="pantalla-santi-punto" />
        {t('oficinaV2.pelotas.pantalla.titulo')}
      </span>
      <span className="mt-1 block h-7 whitespace-nowrap tabular-nums" data-testid="pantalla-santi-cifra">
        <span className={cn('text-[22px] font-bold leading-7', e.claseGrande)}>{e.grande}</span>
        {e.chico ? <span className="text-[13px] font-medium leading-7 text-oficina-texto2"> {e.chico}</span> : null}
      </span>
      <span className={cn('mt-0.5 block h-4 truncate text-[11px] leading-4', e.clasePie)} data-testid="pantalla-santi-pie">
        {e.pie}
      </span>
    </button>
  );
}
